import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Play, Download, Crown, ArrowRight, LogOut, Settings, FileText, Video, ChevronRight, Eye, Clock, Lock, Menu, X, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MemberArea() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Video filter state
  const [videoSearch, setVideoSearch] = useState('');
  const [videoCategory, setVideoCategory] = useState('all');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/dashboard`, { headers: getHeaders() });
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  }, [navigate]);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/exclusive-articles`, { headers: getHeaders() });
      setArticles(res.data.articles || []);
    } catch {}
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/learning-videos`, { headers: getHeaders() });
      setVideos(res.data.videos || []);
    } catch {}
  }, []);

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/materials`, { headers: getHeaders() });
      setMaterials(res.data.materials || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'articles') fetchArticles();
    if (activeTab === 'videos') fetchVideos();
    if (activeTab === 'materials') fetchMaterials();
  }, [activeTab, fetchArticles, fetchVideos, fetchMaterials]);

  const openArticle = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/exclusive-articles/${id}`, { headers: getHeaders() });
      setSelectedArticle(res.data);
    } catch {}
  };

  const [viewingMaterial, setViewingMaterial] = useState(null);

  const handleDownload = async (material) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/member/materials/${material.id}/download`, {}, { headers: getHeaders() });
      // If it has HTML content, show in viewer
      if (res.data.html_content) {
        setViewingMaterial({ ...material, html_content: res.data.html_content });
        return;
      }
      // Otherwise open file URL
      if (res.data.file_url && res.data.file_url !== '#') window.open(res.data.file_url, '_blank');
    } catch {}
  };

  const printMaterial = () => {
    if (!viewingMaterial?.html_content) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${viewingMaterial.title}</title>
    <style>body{font-family:'Segoe UI',sans-serif;max-width:800px;margin:2rem auto;padding:0 1rem;color:#1a1a1a;line-height:1.7}
    h1,h2,h3{color:#0a0a0a}table{width:100%;border-collapse:collapse;margin:1rem 0}
    th,td{border:1px solid #ddd;padding:0.5rem;text-align:left}
    input[type=checkbox]{margin-right:0.5rem}@media print{body{margin:0}}</style>
    </head><body><h1>${viewingMaterial.title}</h1>${viewingMaterial.html_content}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Extract video categories from tags/source (must be before early returns)
  const videoCategories = useMemo(() => {
    const cats = new Set();
    videos.forEach(v => {
      if (v.category) cats.add(v.category);
      if (v.source) cats.add(v.source);
      (v.tags || []).forEach(t => cats.add(t));
    });
    return ['all', ...Array.from(cats)].filter(Boolean);
  }, [videos]);

  const filteredVideos = useMemo(() => {
    let filtered = videos;
    if (videoCategory !== 'all') {
      filtered = filtered.filter(v =>
        v.category === videoCategory || v.source === videoCategory || (v.tags || []).includes(videoCategory)
      );
    }
    if (videoSearch.trim()) {
      const q = videoSearch.toLowerCase();
      filtered = filtered.filter(v =>
        (v.title || '').toLowerCase().includes(q) ||
        (v.channel_title || '').toLowerCase().includes(q) ||
        (v.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [videos, videoCategory, videoSearch]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-pulse" style={{ color: '#d9fb06', fontSize: '1.1rem' }}>Memuat member area...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <Crown size={16} /> },
    { id: 'articles', label: 'Artikel Eksklusif', icon: <BookOpen size={16} /> },
    { id: 'videos', label: 'Video Pembelajaran', icon: <Video size={16} /> },
    { id: 'materials', label: 'Materi Download', icon: <Download size={16} /> },
  ];

  const cardBg = 'rgba(255,255,255,0.03)';
  const cardBorder = '1px solid rgba(255,255,255,0.08)';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#EDEDED' }} data-testid="member-area">
      <style>{`
        @media (max-width: 768px) {
          .member-sidebar-toggle { display: flex !important; }
          .member-username { display: none !important; }
          .member-layout { padding: 1rem !important; gap: 0 !important; }
          .member-sidebar {
            position: fixed !important;
            top: 0; left: -260px;
            width: 240px !important;
            height: 100vh;
            background: #111 !important;
            border-right: 1px solid rgba(255,255,255,0.1);
            z-index: 50;
            padding: 1rem;
            padding-top: 4rem;
            transition: left 0.25s ease;
            overflow-y: auto;
          }
          .member-sidebar.open { left: 0 !important; }
          .member-sidebar-overlay { display: block !important; }
          .member-content { width: 100% !important; min-width: 0 !important; }
        }
      `}</style>
      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="member-sidebar-toggle"
            style={{ display: 'none', background: 'none', border: 'none', color: '#A1A1AA', cursor: 'pointer', padding: '0.25rem' }}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: '#d9fb06' }}>Pakde Heru</span>
          </Link>
          <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(217,251,6,0.15)', color: '#d9fb06' }}>
            MEMBER
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="member-username" style={{ fontSize: '0.85rem', color: '#A1A1AA' }}>{user?.name || user?.email}</span>
          {isAdmin && (
            <Link to="/admin" style={{ fontSize: '0.8rem', color: '#818cf8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }} data-testid="go-to-admin">
              <Settings size={14} /> Admin
            </Link>
          )}
          <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', color: '#71717A', cursor: 'pointer', fontSize: '0.8rem' }} data-testid="member-logout">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </header>

      <div className="member-layout" style={{ display: 'flex', maxWidth: '1280px', margin: '0 auto', padding: '1.5rem', gap: '1.5rem' }}>
        {/* Sidebar overlay for mobile */}
        {sidebarOpen && (
          <div
            className="member-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
            style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }}
          />
        )}
        {/* Sidebar */}
        <aside className={`member-sidebar ${sidebarOpen ? 'open' : ''}`} style={{ width: '220px', flexShrink: 0 }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedArticle(null); setSidebarOpen(false); }}
                data-testid={`member-tab-${tab.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left',
                  background: activeTab === tab.id ? 'rgba(217,251,6,0.1)' : 'transparent',
                  color: activeTab === tab.id ? '#d9fb06' : '#A1A1AA',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="member-content" style={{ flex: 1, minWidth: 0 }}>
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboard && (
            <div data-testid="member-dashboard">
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                Selamat Datang, {dashboard.user?.name || 'Member'}
              </h2>
              <p style={{ color: '#71717A', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Akses konten premium dan materi pembelajaran behavioral finance
              </p>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '2rem' }}>
                {[
                  { label: 'Artikel Eksklusif', value: dashboard.stats?.exclusive_articles || 0, icon: <BookOpen size={20} />, color: '#818cf8' },
                  { label: 'Video Pembelajaran', value: dashboard.stats?.learning_videos || 0, icon: <Play size={20} />, color: '#38bdf8' },
                  { label: 'Materi Download', value: dashboard.stats?.materials || 0, icon: <Download size={20} />, color: '#4ade80' },
                ].map((s, i) => (
                  <div key={i} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div style={{ color: s.color }}>{s.icon}</div>
                      <span style={{ fontSize: '0.8rem', color: '#71717A' }}>{s.label}</span>
                    </div>
                    <p style={{ fontSize: '2rem', fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Recent articles */}
              {dashboard.recent_articles?.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Artikel Terbaru</h3>
                    <button onClick={() => setActiveTab('articles')} style={{ background: 'none', border: 'none', color: '#d9fb06', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Lihat Semua <ChevronRight size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {dashboard.recent_articles.map(a => (
                      <div key={a.id} onClick={() => { setActiveTab('articles'); openArticle(a.id); }} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', padding: '1rem', cursor: 'pointer', transition: 'border-color 0.15s' }}>
                        <h4 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1rem', marginBottom: '0.375rem', color: '#EDEDED' }}>{a.title}</h4>
                        <p style={{ fontSize: '0.8rem', color: '#71717A', lineHeight: 1.4 }}>{a.excerpt}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent videos */}
              {dashboard.recent_videos?.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Video Terbaru</h3>
                    <button onClick={() => setActiveTab('videos')} style={{ background: 'none', border: 'none', color: '#d9fb06', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Lihat Semua <ChevronRight size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '0.75rem' }}>
                    {dashboard.recent_videos.map(v => (
                      <div key={v.id} onClick={() => setActiveTab('videos')} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', overflow: 'hidden', cursor: 'pointer' }}>
                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#1a1a1a' }}>
                          <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt="" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                            <Play size={36} color="#fff" fill="#fff" style={{ opacity: 0.9 }} />
                          </div>
                        </div>
                        <div style={{ padding: '0.75rem' }}>
                          <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', lineHeight: 1.3 }}>{v.title}</h4>
                          <span style={{ fontSize: '0.7rem', color: '#71717A' }}>{v.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Articles Tab */}
          {activeTab === 'articles' && (
            <div data-testid="member-articles">
              {selectedArticle ? (
                <div>
                  <button onClick={() => setSelectedArticle(null)} style={{ background: 'none', border: 'none', color: '#d9fb06', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    ← Kembali ke daftar
                  </button>
                  <article style={{ background: cardBg, border: cardBorder, borderRadius: '1rem', padding: '2rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      {selectedArticle.tags?.map((t, i) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem', background: 'rgba(217,251,6,0.1)', color: '#d9fb06' }}>{t}</span>
                      ))}
                    </div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', lineHeight: 1.3, marginBottom: '0.75rem' }}>{selectedArticle.title}</h1>
                    <p style={{ color: '#A1A1AA', fontSize: '0.9rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>{selectedArticle.excerpt}</p>
                    <div style={{ fontSize: '0.95rem', lineHeight: 1.8, color: '#D4D4D8' }} dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                  </article>
                </div>
              ) : (
                <div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '1rem' }}>Artikel Eksklusif</h2>
                  {articles.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#71717A' }}>
                      <BookOpen size={40} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
                      <p>Belum ada artikel eksklusif. Segera hadir!</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {articles.map(a => (
                        <div key={a.id} onClick={() => openArticle(a.id)} data-testid={`article-card-${a.id}`} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', padding: '1.25rem', cursor: 'pointer', transition: 'border-color 0.15s' }}>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(129,140,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Lock size={18} color="#818cf8" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', marginBottom: '0.375rem', lineHeight: 1.3 }}>{a.title}</h3>
                              <p style={{ fontSize: '0.85rem', color: '#71717A', lineHeight: 1.4, marginBottom: '0.5rem' }}>{a.excerpt}</p>
                              <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#52525B' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Eye size={12} /> {a.views || 0} views</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={12} /> {a.created_at ? new Date(a.created_at).toLocaleDateString('id-ID') : ''}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>{a.category}</span>
                              </div>
                            </div>
                            <ArrowRight size={16} color="#71717A" style={{ flexShrink: 0, marginTop: '0.5rem' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div data-testid="member-videos">
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '1rem' }}>Video Pembelajaran</h2>

              {/* Search & Category Filter */}
              {videos.length > 0 && (
                <div style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* Search bar */}
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#52525B' }} />
                    <input
                      type="text"
                      value={videoSearch}
                      onChange={e => setVideoSearch(e.target.value)}
                      placeholder="Cari video berdasarkan judul, channel, atau tag..."
                      data-testid="video-search-input"
                      style={{
                        width: '100%', padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                        borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.03)', color: '#EDEDED', fontSize: '0.85rem',
                        outline: 'none',
                      }}
                    />
                  </div>
                  {/* Category pills */}
                  <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                    {videoCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setVideoCategory(cat)}
                        data-testid={`video-cat-${cat}`}
                        style={{
                          padding: '0.3rem 0.7rem', borderRadius: '2rem', border: 'none', cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: videoCategory === cat ? 600 : 400,
                          background: videoCategory === cat ? 'rgba(217,251,6,0.15)' : 'rgba(255,255,255,0.05)',
                          color: videoCategory === cat ? '#d9fb06' : '#A1A1AA',
                          transition: 'all 0.15s',
                        }}
                      >
                        {cat === 'all' ? 'Semua' : cat}
                      </button>
                    ))}
                  </div>
                  {/* Result count */}
                  {(videoSearch || videoCategory !== 'all') && (
                    <p style={{ fontSize: '0.8rem', color: '#71717A' }}>
                      {filteredVideos.length} video ditemukan
                      {videoSearch && <> untuk "{videoSearch}"</>}
                      {videoCategory !== 'all' && <> di kategori "{videoCategory}"</>}
                    </p>
                  )}
                </div>
              )}

              {videos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#71717A' }}>
                  <Video size={40} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
                  <p>Belum ada video. Segera hadir!</p>
                </div>
              ) : filteredVideos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#71717A' }}>
                  <Search size={32} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                  <p>Tidak ada video yang cocok dengan filter.</p>
                  <button
                    onClick={() => { setVideoSearch(''); setVideoCategory('all'); }}
                    style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#d9fb06', cursor: 'pointer', fontSize: '0.85rem' }}
                  >Reset Filter</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1rem' }}>
                  {filteredVideos.map(v => (
                    <div key={v.id} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', overflow: 'hidden' }} data-testid={`video-card-${v.id}`}>
                      <div style={{ position: 'relative', paddingTop: '56.25%', background: '#111' }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${v.youtube_id}`}
                          title={v.title}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      </div>
                      <div style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.375rem' }}>
                          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.3, flex: 1 }}>{v.title}</h3>
                          {v.duration && <span style={{ fontSize: '0.7rem', color: '#71717A', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px', flexShrink: 0, marginLeft: '0.5rem' }}>{v.duration}</span>}
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#71717A', lineHeight: 1.4 }}>{v.description}</p>
                        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                          {v.category && (
                            <span
                              onClick={() => setVideoCategory(v.category)}
                              style={{ fontSize: '0.7rem', cursor: 'pointer', padding: '2px 8px', borderRadius: '8px', background: 'rgba(56,189,248,0.1)', color: '#38bdf8' }}
                            >{v.category}</span>
                          )}
                          {(v.tags || []).map((tag, i) => (
                            <span
                              key={i}
                              onClick={() => setVideoCategory(tag)}
                              style={{ fontSize: '0.7rem', cursor: 'pointer', padding: '2px 8px', borderRadius: '8px', background: 'rgba(217,251,6,0.08)', color: '#a3c904' }}
                            >#{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Materials Tab */}
          {activeTab === 'materials' && (
            <div data-testid="member-materials">
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '1rem' }}>Materi Download</h2>

              {/* Material Viewer */}
              {viewingMaterial && (
                <div style={{ marginBottom: '1.5rem', background: cardBg, border: cardBorder, borderRadius: '1rem', overflow: 'hidden' }} data-testid="material-viewer">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>{viewingMaterial.title}</h3>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={printMaterial} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: '0.8rem' }}>
                        <Download size={14} /> Print / Save PDF
                      </button>
                      <button onClick={() => setViewingMaterial(null)} style={{ padding: '0.375rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.08)', color: '#A1A1AA', fontSize: '0.8rem' }}>
                        Tutup
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '1.5rem', maxHeight: '500px', overflowY: 'auto', fontSize: '0.9rem', lineHeight: 1.7, color: '#D4D4D8' }} dangerouslySetInnerHTML={{ __html: viewingMaterial.html_content }} />
                </div>
              )}

              {materials.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#71717A' }}>
                  <Download size={40} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
                  <p>Belum ada materi. Segera hadir!</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {materials.map(m => (
                    <div key={m.id} style={{ background: cardBg, border: cardBorder, borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }} data-testid={`material-card-${m.id}`}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: m.category === 'Worksheet' ? 'rgba(251,191,36,0.1)' : m.category === 'Ringkasan' ? 'rgba(129,140,248,0.1)' : 'rgba(74,222,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FileText size={22} color={m.category === 'Worksheet' ? '#fbbf24' : m.category === 'Ringkasan' ? '#818cf8' : '#4ade80'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.25rem' }}>{m.title}</h3>
                        <p style={{ fontSize: '0.8rem', color: '#71717A', lineHeight: 1.3 }}>{m.description}</p>
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.375rem', fontSize: '0.7rem', color: '#52525B' }}>
                          <span style={{ padding: '1px 6px', borderRadius: '6px', background: 'rgba(255,255,255,0.05)' }}>{m.category || m.file_type?.toUpperCase()}</span>
                          {m.file_size && <span>{m.file_size}</span>}
                          <span>{m.download_count || 0} downloads</span>
                          {m.auto_generated && <span style={{ color: '#d9fb06' }}>Auto-generated</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDownload(m)} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: '0.85rem', fontWeight: 500, flexShrink: 0 }} data-testid={`download-btn-${m.id}`}>
                        <Download size={14} /> {m.html_content || m.file_type === 'html' ? 'Buka' : 'Download'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
